import {useMemo,useState} from 'react';
import {acceptCommunityInvitation} from '../api';
import {loginCommunity,registerCommunity,type CommunityAuthSession} from '../auth';
import {clearPendingInvitationToken,pendingInvitationToken} from '../invitations';

type Mode='login'|'register';
type Props={initialError?:string;onAuthenticated:()=>void};

function registrationCodeFromUrl():string{
 const params=new URLSearchParams(window.location.search);
 return params.get('invite')||params.get('invitation')||params.get('code')||'';
}

export function CommunityAuthGate({initialError='',onAuthenticated}:Props){
 const registrationCode=useMemo(registrationCodeFromUrl,[]);
 const roomInvitation=useMemo(()=>pendingInvitationToken(),[]);
 const[mode,setMode]=useState<Mode>(registrationCode?'register':'login');
 const[name,setName]=useState('');
 const[email,setEmail]=useState('');
 const[accessCode,setAccessCode]=useState('');
 const[inviteCode,setInviteCode]=useState(registrationCode);
 const[error,setError]=useState(initialError);
 const[busy,setBusy]=useState(false);
 const[pending,setPending]=useState<CommunityAuthSession|null>(null);
 const[copied,setCopied]=useState(false);

 function changeMode(next:Mode){setMode(next);setError('');setPending(null);setCopied(false);}
 async function acceptRoomInvitation(){
  if(!roomInvitation)return;
  await acceptCommunityInvitation(roomInvitation);
  clearPendingInvitationToken();
 }
 async function submitLogin(event:React.FormEvent<HTMLFormElement>){
  event.preventDefault();setBusy(true);setError('');
  try{await loginCommunity(email,accessCode);await acceptRoomInvitation();onAuthenticated();}
  catch(reason){setError(reason instanceof Error?reason.message:'Der Zugang konnte nicht geöffnet werden.');}
  finally{setBusy(false);}
 }
 async function submitRegistration(event:React.FormEvent<HTMLFormElement>){
  event.preventDefault();setBusy(true);setError('');
  try{
   const session=await registerCommunity(name,email,inviteCode);
   setPending(session);
   try{await acceptRoomInvitation();}
   catch(reason){
    const message=reason instanceof Error?reason.message:'Die Raum-Einladung konnte nicht angenommen werden.';
    setError(`Dein persönlicher Zugang wurde angelegt. Bewahre den unten angezeigten Code zuerst sicher auf. ${message}`);
   }
  }
  catch(reason){setError(reason instanceof Error?reason.message:'Der Zugang konnte nicht angelegt werden.');}
  finally{setBusy(false);}
 }
 async function copyAndEnter(){
  if(!pending)return;
  if(pending.access_code){try{await navigator.clipboard.writeText(pending.access_code);setCopied(true);}catch{setCopied(false);}}
  onAuthenticated();
 }

 return <main className="community-auth">
  <section className="community-auth-card" aria-labelledby="community-auth-title">
   <img src="/kr-logo.svg" alt="Katrin & Rayk"/>
   <span className="community-auth-eyebrow">GESCHÜTZTE COMMUNITY</span>
   <h1 id="community-auth-title">Deine Räume.<br/>Dein Zugang.</h1>
   <p className="community-auth-intro">Melde dich direkt hier an. Dein Zugang bleibt auf diesem Gerät gespeichert.</p>
   {roomInvitation&&<div className="community-invitation-notice">
    <strong>Eine persönliche Raum-Einladung wartet auf dich.</strong>
    <span>Mit einem bestehenden persönlichen Code meldest du dich direkt an. Beim allerersten Zugang wählst du „Erster Zugang“ und verwendest zusätzlich den allgemeinen Einladungscode.</span>
   </div>}
   {!pending&&<>
    <div className="community-auth-tabs" role="tablist" aria-label="Zugang wählen">
     <button type="button" role="tab" aria-selected={mode==='login'} className={mode==='login'?'active':''} onClick={()=>changeMode('login')}>Schon registriert</button>
     <button type="button" role="tab" aria-selected={mode==='register'} className={mode==='register'?'active':''} onClick={()=>changeMode('register')}>Erster Zugang</button>
    </div>
    {mode==='login'?<form className="community-auth-form" onSubmit={submitLogin}>
     <label><span>Deine E-Mail</span><input type="email" inputMode="email" autoComplete="email" value={email} onChange={event=>setEmail(event.target.value)} required/></label>
     <label><span>Dein persönlicher Zugangscode</span><input type="password" autoComplete="current-password" placeholder="XXXX-XXXX" value={accessCode} onChange={event=>setAccessCode(event.target.value)} required/></label>
     <small>Verwende deinen persönlichen Code, nicht den gemeinsamen Einladungscode.</small>
     <button className="community-auth-primary" disabled={busy}>{busy?'Der Raum öffnet sich …':'In meine Räume eintreten'}</button>
    </form>:<form className="community-auth-form" onSubmit={submitRegistration}>
     <label><span>Dein Name</span><input autoComplete="name" maxLength={80} value={name} onChange={event=>setName(event.target.value)} required/></label>
     <label><span>Deine E-Mail</span><input type="email" inputMode="email" autoComplete="email" value={email} onChange={event=>setEmail(event.target.value)} required/></label>
     <label><span>Allgemeiner Einladungscode</span><input autoComplete="off" autoCapitalize="characters" spellCheck={false} value={inviteCode} onChange={event=>setInviteCode(event.target.value)} required/></label>
     <small>Danach erhältst du einmalig deinen persönlichen Zugangscode. Eine mitgeschickte Raum-Einladung wird automatisch angenommen.</small>
     <button className="community-auth-primary" disabled={busy}>{busy?'Dein Zugang entsteht …':'Persönlichen Zugang anlegen'}</button>
    </form>}
   </>}
   {pending&&<div className="community-access-result">
    <span>DEIN PERSÖNLICHER CODE</span>
    <strong>{pending.access_code||'Bereits angelegt'}</strong>
    <p>Bewahre diesen Code sicher auf. Zusammen mit deiner E-Mail öffnet er deinen Zugang auch auf einem anderen Gerät.</p>
    <button className="community-auth-primary" onClick={copyAndEnter}>{pending.access_code?(copied?'Kopiert · Räume öffnen':'Code kopieren und Räume öffnen'):'Räume öffnen'}</button>
   </div>}
   {error&&<p className="community-auth-error" role="alert">{error}</p>}
   <p className="community-auth-note">Journal, Tresor und privates Postfach bleiben geschützt. Nur bewusst geteilte Inhalte werden in gemeinsamen Räumen sichtbar.</p>
  </section>
 </main>;
}
